import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleRow from '@/components/feature/ArticleRow';
import Pagination from '@/components/feature/Pagination';
import { newsList } from '@/mocks/news';
import { searchNews, sortByDateDesc } from '@/utils/news';
import { useSEO } from '@/hooks/useSEO';

const PAGE_SIZE = 5;
const HISTORY_KEY = 'news911:searchHistory';

function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const pageParam = Number(params.get('page') ?? '1');
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const [input, setInput] = useState(query);
  const [history, setHistory] = useState<string[]>([]);

  useSEO({
    title: query ? `“${query}”的搜索结果 - 911爆料网` : '搜索资讯 - 911爆料网',
    description: query
      ? `911爆料网关于“${query}”的搜索结果，为你汇总相关爆料与社会热点资讯。`
      : '在911爆料网搜索社会热点、突发新闻与民生爆料。',
    keywords: '搜索,911爆料网,爆料新闻,社会热点',
  });

  useEffect(() => {
    setInput(query);
    setHistory(readHistory());
  }, [query]);

  const saveHistory = (q: string) => {
    const next = [q, ...readHistory().filter((item) => item !== q)].slice(0, 8);
    setHistory(next);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };

  const results = useMemo(() => (query ? searchNews(query) : []), [query]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const recommended = sortByDateDesc(newsList).slice(0, 8);

  const submit = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    saveHistory(trimmed);
    setParams({ q: trimmed });
  };

  const goPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set('page', String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

        <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            站内搜索
          </h1>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
            className="mt-4 flex flex-col gap-2 sm:flex-row"
            role="search"
          >
            <div className="flex min-w-0 flex-1 items-center rounded-md border border-background-300 bg-background-50 px-4 py-2.5">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如“电梯”“菜市场”“装修”"
                className="mx-2 min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="搜索关键词"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>

          {history.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">历史搜索</span>
              {history.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => submit(h)}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {h}
                </button>
              ))}
            </div>
          )}
        </div>

        {query && (
          <p className="mt-5 text-sm text-foreground-600">
            关于
            <span className="mx-1 font-semibold text-primary-600">“{query}”</span>
            的搜索结果，共
            <span className="mx-1 font-semibold text-secondary-600">{results.length}</span>
            条
          </p>
        )}

        {query && pageItems.length > 0 && (
          <>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
              {pageItems.map((article) => (
                <ArticleRow key={article.id} article={article} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={goPage} />
          </>
        )}

        {query && results.length === 0 && (
          <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-50 p-6 md:p-8">
            <div className="flex flex-col items-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                没有找到与“{query}”相关的内容
              </h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词试试，或者看看下面这些大家正在关注的热门爆料。
              </p>
              <Link
                to="/"
                className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
            </div>

            <h3 className="mt-8 mb-3 flex items-center gap-2 border-b border-background-200 pb-2 font-heading text-base font-bold text-foreground-900">
              <i className="ri-fire-line text-secondary-500"></i>
              热门推荐
            </h3>
            <div className="flex flex-col">
              {recommended.slice(0, 5).map((item) => (
                <ArticleRow key={item.id} article={item} />
              ))}
            </div>
          </div>
        )}

        {!query && (
          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-fire-line text-secondary-500"></i>
              大家都在看
            </h2>
            <div className="mt-3 flex flex-col">
              {recommended.slice(0, 6).map((item) => (
                <ArticleRow key={item.id} article={item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}